import React, { useEffect, useState } from 'react';
import { FormDialog, InputField, MuiBox, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { KioskHardware } from 'store/kioskHardware';
import { getSortedList } from 'utils/stringFormat';
import { Hardware, selectors as hardwareSelectors } from 'store/hardware';
import { kioskHardwareValidationSchema } from 'views/KiosksView/utils';
import { INVALID, VALID } from 'views/KiosksView/constants';
import { Mode } from 'views/KiosksView/types';
import { useKioskFormStyles } from '../styled';

interface KioskHardwareFormProps {
  mode: Mode;
  initialData?: KioskHardware;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (payload: any) => void;
}

export const KioskHardwareForm: React.FC<KioskHardwareFormProps> = (props) => {
  const { mode, initialData, onClose, onSubmit } = props;

  const isEdit = mode === 'edit';
  const formClasses = useKioskFormStyles();

  const hardwareList = useSelector(hardwareSelectors.selectHardware);
  const sortedHardware = getSortedList(hardwareList, 'name');

  const [formData, setFormData] = useState({
    kioskId: '',
    hardwareId: '',
    serialCode: '',
    configuration: ''
  });

  const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(null);

  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  useEffect(() => {
    if (isEdit && initialData) {
      setFormData(initialData);

      const hw = sortedHardware.find((h: Hardware) => h.id === initialData.hardwareId);
      setSelectedHardware(hw || null);
    }
  }, [initialData]);

  /**
   * INPUT CHANGE
   */
  const onInputChange = (key: keyof typeof formData, e: React.ChangeEvent<HTMLInputElement>) => {
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setFormData({
      ...formData,
      [key]: e.target.value
    });
  };

  const onHardwareChange = (item: Hardware) => {
    setSelectedHardware(item);

    setFormData({
      ...formData,
      hardwareId: item.id
    });
  };

  /**
   * INPUT BLUR VALIDATION
   */
  const onInputBlur = async (
    key: keyof typeof formData,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    const trimmed = e.target.value.trim();

    setFormData({
      ...formData,
      [key]: trimmed
    });

    let invalidMessage = null;

    if (value && value.trim().length) {
      const result = await kioskHardwareValidationSchema.validate({ [key]: value.trim() }).catch((err) => err);
      invalidMessage = result?.message;
    }

    setValidation({
      ...validation,
      [key]: invalidMessage ? INVALID : VALID
    });

    setValidationMessage({
      ...validationMessage,
      [key]: invalidMessage
    });
  };

  const validate = (key: keyof typeof formData) => {
    return validationMessage[key];
  };

  /**
   * SUBMIT
   */
  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <FormDialog
      open
      title={isEdit ? 'Edit Hardware' : 'Add Hardware'}
      confirmButtonText={isEdit ? 'Save' : 'Add'}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
      confirmButtonAction={handleSubmit}
      confirmButtonDisabled={!formData.hardwareId}
    >
      <MuiBox mt="14px">
        {/* Hardware Dropdown */}
        <MuiBox className={formClasses.formOverlayInputBox}>
          <Typography className={formClasses.formDropdownLabel}>Hardware</Typography>

          <SelectDropdown
            placeholder="Select Hardware"
            items={sortedHardware}
            selectedItem={selectedHardware}
            onChange={(item: Hardware) => onHardwareChange(item)}
            itemToString={(item: Hardware) => (item ? `${item.name} (${item.type})` : '')}
            radius="round"
            width="524px"
            sx={{
              height: '45px',
              '& .MuiSelect-select': {
                color: selectedHardware ? '#000' : '#9E9E9E'
              }
            }}
          />
        </MuiBox>
        <MuiBox className={formClasses.formOverlayInputBox}>
          <InputField
            label="Serial Code"
            size="small"
            fullWidth
            sx={{ width: '262px', height: '45px' }}
            value={formData.serialCode || ''}
            onChange={(e) => onInputChange('serialCode', e)}
            onBlur={(e) => onInputBlur('serialCode', e, e.target.value)}
            helperText={validate('serialCode')}
            error={validation.serialCode === INVALID}
          />
        </MuiBox>
        <MuiBox>
          <InputField
            multiline
            label="Configuration"
            size="small"
            rows={16}
            fullWidth
            sx={{ width: '524px', height: '400px', borderRadius: '25px' }}
            value={
              typeof formData?.configuration === 'object'
                ? JSON.stringify(formData?.configuration)
                : String(formData?.configuration) || ''
            }
            onChange={(e) => onInputChange('configuration', e)}
            onBlur={(e) => onInputBlur('configuration', e, e.target.value)}
            helperText={validate('configuration')}
            error={validation.configuration === INVALID}
          />
        </MuiBox>
      </MuiBox>
    </FormDialog>
  );
};
